import React, { Component } from "react";
import { ENABLE_STRIPE } from "../../config/featureFlags";
import withStyles from "@material-ui/core/styles/withStyles";
import {
  Switch,
  Typography,
  Theme,
  Select,
  MenuItem,
  Input,
  WithStyles,
  createStyles,
  InputAdornment,
  Button,
  Box,
  CircularProgress,
} from "@material-ui/core";
import { PaymentRules, PaymentRuleFields } from "../../types/game";
import VideoPopup from "components/SharedComponents/VideoPopup";
import { HELP_VIDEOS } from "consts";
import paymentApi from "../../api/payment";
import Config, { isOrganiserStripeReadyForPaidGames } from "config";
import { User } from "../../types/user";
import { estimateOrganiserNetGbp } from "../../utils/stripeOrganiserEstimate";
import {
  defaultPaymentRulesForPaidGame,
  normalizeCollectionForPayBeforePlay,
} from "../../utils/paymentCollectionMethod";
import { numberInputProps } from "../../utils/formNumbers";

export interface Props extends WithStyles {
  isPaid: boolean;
  isRangeGame?: boolean;
  paymentRules: PaymentRules;
  disabled?: boolean;
  user?: User;
  gameId?: string;
  /** When false, Stripe setup prompts are hidden (game owner handles Connect). Defaults to true for new games. */
  isGameOwner?: boolean;
  onPaymentRulesChanged: (value: PaymentRules) => void;
  onPaidSwitchChanged: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onOpenStripeSetup?: () => void;
}

interface State {
  connectReadyFromApi: boolean | null;
  stripeStatusLoading: boolean;
  feeDraft: string | null;
}

class GamePayment extends Component<Props, State> {
  state: State = { connectReadyFromApi: null, stripeStatusLoading: false, feeDraft: null };

  onPaymentRulesChange = (field: PaymentRuleFields, value: number | boolean) => {
    const next = normalizeCollectionForPayBeforePlay({
      ...defaultPaymentRulesForPaidGame(this.props.paymentRules),
      ...this.props.paymentRules,
      [field]: value,
    });
    this.props.onPaymentRulesChanged(next);
    if (field === PaymentRuleFields.PAY_BEFORE_PLAY) {
      this.syncStripeStatusCheck(next);
    }
  };

  componentDidMount() {
    if (this.props.isPaid) {
      this.syncStripeStatusCheck(this.props.paymentRules);
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.user?._id !== this.props.user?._id) {
      this.syncStripeStatusCheck(this.props.paymentRules);
      return;
    }
    if (!prevProps.isPaid && this.props.isPaid) {
      this.setState({ feeDraft: null });
      this.syncStripeStatusCheck(this.props.paymentRules);
      return;
    }
    if (prevProps.isPaid && !this.props.isPaid) {
      this.setState({ connectReadyFromApi: null, stripeStatusLoading: false, feeDraft: null });
    }
  }

  getFeeDisplay = (): string => {
    if (this.state.feeDraft !== null) return this.state.feeDraft;
    const fee = defaultPaymentRulesForPaidGame(this.props.paymentRules).fee;
    return fee > 0 ? String(fee) : "";
  };

  onFeeChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const raw = evt.target.value;
    this.setState({ feeDraft: raw });
    const parsed = parseFloat(raw);
    if (raw !== "" && raw !== "." && Number.isFinite(parsed) && parsed >= 0) {
      this.onPaymentRulesChange(PaymentRuleFields.FEE, parsed);
    }
  };

  onFeeBlur = () => {
    const raw = this.state.feeDraft;
    this.setState({ feeDraft: null });
    if (raw === null) return;
    const trimmed = raw.trim();
    if (trimmed === "" || trimmed === ".") {
      this.onPaymentRulesChange(PaymentRuleFields.FEE, 0);
      return;
    }
    const parsed = parseFloat(trimmed);
    this.onPaymentRulesChange(PaymentRuleFields.FEE, Number.isFinite(parsed) && parsed >= 0 ? parsed : 0);
  };

  syncStripeStatusCheck = (rules?: PaymentRules) => {
    if (!ENABLE_STRIPE || !this.props.isPaid) {
      this.setState({ connectReadyFromApi: null, stripeStatusLoading: false });
      return;
    }
    this.loadStripeConnectStatus();
  };

  loadStripeConnectStatus = () => {
    if (!ENABLE_STRIPE) {
      this.setState({ connectReadyFromApi: null, stripeStatusLoading: false });
      return;
    }
    if (Config.DEV_BYPASS_STRIPE_CONNECT_READINESS) {
      this.setState({ connectReadyFromApi: true, stripeStatusLoading: false });
      return;
    }
    this.setState({ stripeStatusLoading: true });
    const statusRequest = this.props.gameId
      ? paymentApi.getStripeConnectStatusForGame(this.props.gameId)
      : paymentApi.getStripeConnectStatus();

    statusRequest
      .then((res: { data: { ready?: boolean } }) => {
        this.setState({ connectReadyFromApi: !!res.data.ready, stripeStatusLoading: false });
      })
      .catch(() => {
        this.setState({
          connectReadyFromApi: isOrganiserStripeReadyForPaidGames(this.props.user),
          stripeStatusLoading: false,
        });
      });
  };

  effectiveStripeConnectReady(): boolean {
    if (Config.DEV_BYPASS_STRIPE_CONNECT_READINESS) return true;
    if (this.state.connectReadyFromApi !== null) return this.state.connectReadyFromApi;
    return isOrganiserStripeReadyForPaidGames(this.props.user);
  }

  renderStripeSetupPrompt() {
    const { onOpenStripeSetup } = this.props;
    return (
      <Box mb={2} p={1.5} bgcolor="#fff8e6" borderRadius={4}>
        <Typography variant="body2" color="textSecondary" paragraph style={{ marginBottom: 8 }}>
          Connect Stripe in your profile to accept card payments (Schala fee: 4% + 20p per charge).
        </Typography>
        <Button variant="contained" color="primary" size="small" onClick={onOpenStripeSetup}>
          Set up Stripe in profile
        </Button>
      </Box>
    );
  }

  renderOwnerStripePendingNotice() {
    return (
      <Box mb={2} p={1.5} bgcolor="#f5f5f5" borderRadius={4}>
        <Typography variant="body2" color="textSecondary" paragraph style={{ marginBottom: 0 }}>
          Card payments are unavailable until the game owner completes Stripe setup in their profile.
        </Typography>
      </Box>
    );
  }

  render() {
    const { classes, isPaid, isRangeGame, paymentRules, disabled } = this.props;
    const isGameOwner = this.props.isGameOwner !== false;
    const rules = defaultPaymentRulesForPaidGame(paymentRules);
    const stripeConnectReady = this.effectiveStripeConnectReady();
    const stripeCheckPending = isPaid && this.state.stripeStatusLoading;

    return (
      <div>
        <div className={classes.inline}>
          <Typography variant="subtitle1">In-app payments</Typography>
          <Switch
            name="isPaid"
            checked={isPaid}
            disabled={disabled}
            onChange={e => this.props.onPaidSwitchChanged(e)}
          />
        </div>

        {!isPaid ? null : (
          <div>
            <Typography variant="caption" color="textSecondary" display="block" paragraph>
              {ENABLE_STRIPE
                ? isRangeGame
                  ? "Range games use Stripe Checkout only — no off-app mark-paid."
                  : "In-app cards use Stripe Checkout. Cash or bank transfer: mark players paid from the game menu. Both appear in Received payments."
                : "Cash or bank transfer: mark players paid from the game menu."}
            </Typography>

            {ENABLE_STRIPE && stripeCheckPending ? (
              <Box display="flex" alignItems="center" mb={2}>
                <CircularProgress size={18} style={{ marginRight: 8 }} />
                <Typography variant="body2" color="textSecondary">
                  Checking Stripe setup…
                </Typography>
              </Box>
            ) : ENABLE_STRIPE && !stripeConnectReady ? (
              isGameOwner ? this.renderStripeSetupPrompt() : this.renderOwnerStripePendingNotice()
            ) : null}

            <div className={classes.inline}>
              <Typography variant="subtitle1">Pay before play</Typography>
              <Switch
                checked={rules.payBeforePlay}
                onChange={evt => this.onPaymentRulesChange(PaymentRuleFields.PAY_BEFORE_PLAY, evt.target.checked)}
              />
            </div>
            {rules.payBeforePlay ? (
              <Typography variant="caption" color="textSecondary" display="block" paragraph>
                {ENABLE_STRIPE
                  ? isRangeGame
                    ? "Players pay online to join via Stripe Checkout. You can also add them from the players list. Unpaid players show a red £ until paid and cannot play."
                    : "Players pay online to join via Stripe Checkout. You can also add players and mark payment by other means. Unpaid players show a red £ until paid and cannot play."
                  : "Players must be marked paid to play. You can add players and mark payment by other means. Unpaid players show a red £ until paid and cannot play."}
              </Typography>
            ) : (
              <Typography variant="caption" color="textSecondary" display="block" paragraph>
                Players can join without paying first; they can pay anytime from the game menu. Unpaid players show a red £ on the leaderboard.
              </Typography>
            )}
        
            <div className={classes.inline}>
              <Typography variant={"subtitle1"} className={classes.disabled}>
                Refund Nett on leave
              </Typography>
              <Switch disabled />
            </div>
            <div>
              <Typography variant={"h6"}>Entry fee</Typography>
            </div>
            <div className={classes.inline}>
              <Typography variant={"subtitle1"}>Charge per</Typography>
              <Select
                disabled={disabled}
                value={rules.type}
                onChange={evt => this.onPaymentRulesChange(PaymentRuleFields.TYPE, evt.target.value as number)}
              >
                <MenuItem value={1}>Multiple Player</MenuItem>
                <MenuItem value={2}>Team</MenuItem>
              </Select>
            </div>
            <div className={classes.inline}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Typography variant={"subtitle1"}>Entry amount</Typography>
                <VideoPopup videoURL={HELP_VIDEOS.GAME_SETUP_TRANSACTION} />
              </div>
              <Input
                type={"number"}
                value={this.getFeeDisplay()}
                startAdornment={<InputAdornment position="start">&#163;</InputAdornment>}
                onChange={this.onFeeChange}
                onBlur={this.onFeeBlur}
                inputProps={numberInputProps}
                className={classes.smallInput}
              />
            </div>
            {ENABLE_STRIPE && stripeConnectReady ? (
              <div>
                <div className={classes.inline}>
                  <Typography variant={"subtitle1"}>Your net per card payment (est.)</Typography>
                  <Input
                    type={"number"}
                    disabled
                    startAdornment={<InputAdornment position="start">&#163;</InputAdornment>}
                    value={estimateOrganiserNetGbp(Number(rules.fee) || 0).toFixed(2)}
                    className={classes.smallInput}
                  />
                </div>
                <Typography variant="caption" color="textSecondary" component="p" className={classes.estimateHint}>
                  After Schala and Stripe fees, per in-app card payment.
                </Typography>
              </div>
            ) : null}
          </div>
        )}
      </div>
    );
  }
}

const styles = (theme: Theme) =>
  createStyles({
    inline: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
    },
    disabled: {
      color: "#aaaaaa",
    },
    smallInput: {
      width: 80,
    },
    estimateHint: {
      marginTop: 4,
      maxWidth: 420,
      lineHeight: 1.45,
    },
  });

export default withStyles(styles)(GamePayment);
